export type CalendarOwner = "ella" | "leo";

export interface CalendarAttendee {
  name: string | null;
  email: string | null;
  type: string | null;
  status: string | null;
}

export interface SharedCalendarEvent {
  id: string;
  owner: CalendarOwner;
  ownerEmail: string;
  subject: string;
  start: string;
  end: string;
  isAllDay: boolean;
  location: string | null;
  bodyHtml: string | null;
  bodyPreview: string | null;
  organizer: { name: string | null; email: string | null } | null;
  attendees: CalendarAttendee[];
  categories: string[];
  webLink: string | null;
}

/**
 * Visual color scheme per owner — applied to the event blocks and the
 * legend dots. Hex values are kept alongside Tailwind classes so the
 * dot/bar can render via inline style (avoids Tailwind JIT class-scan
 * misses on dynamic class strings).
 *
 * - Ella → Verhuur afspraken (rental visits, place inspections, …)
 * - Leo  → Onderhoud afspraken (helpdesk interventies, maintenance, …)
 */
export const OWNER_STYLES: Record<
  CalendarOwner,
  {
    label: string;
    description: string;
    accentHex: string; // for inline style on dots/bars
    blockClass: string;
  }
> = {
  ella: {
    label: "Ella",
    description: "Verhuur afspraken",
    accentHex: "#F59E0B", // amber-500
    blockClass: "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100",
  },
  leo: {
    label: "Leo",
    description: "Onderhoud afspraken",
    accentHex: "#2563EB", // blue-600 — clearly distinct from amber
    blockClass: "border-blue-300 bg-blue-50 text-blue-900 hover:bg-blue-100",
  },
};

/**
 * Categorize an event into a friendly "type" label that shows up on the
 * block. Falls back to the calendar category, then to a generic label.
 */
export const inferEventType = (event: SharedCalendarEvent): string => {
  const subject = event.subject.toLowerCase();
  if (event.categories?.length) return event.categories[0];
  if (subject.startsWith("h") && /^h\d+/.test(subject)) return "Helpdesk";
  if (subject.includes("rental") || subject.includes("appointment"))
    return "Rental afspraak";
  if (subject.includes("plaatsbeschrijving")) return "Plaatsbeschrijving";
  if (subject.includes("interventie")) return "Interventie";
  return "Afspraak";
};
